/**
 * RegressionResultsSection Component
 * 
 * Displays regression chart results with EChartsRegressionChart.
 * Handles chart rendering, data point toggling, and mobile warnings.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '@/shared/components/forms/inputs/InputField';
import EChartsRegressionChart from '@/shared/components/charts/EChartsRegressionChart';
import type { DataPoint } from '../types/dataCloud.types';
import { germanNumberFormatter } from '../utils/dataTransformers';

export interface RegressionResultsSectionProps {
  /** Processed chart data points */
  chartData: DataPoint[];
  /** Raw data points before sampling */
  rawData: DataPoint[];
  /** X-axis label extracted from file headers */
  xAxisLabel: string;
  /** Y-axis label extracted from file headers */
  yAxisLabel: string;
  /** Chart point size */
  pointSize: string;
  /** Whether data is currently loading */
  isLoading: boolean;
  /** Callback to change point size */
  onPointSizeChange: (value: string) => void;
}

export const RegressionResultsSection: React.FC<RegressionResultsSectionProps> = ({
  chartData,
  rawData,
  xAxisLabel,
  yAxisLabel,
  pointSize,
  isLoading,
  onPointSizeChange,
}) => {
  const { t } = useTranslation('dataCloud');

  // Memoize the arrays handed to ECharts so their identity is stable across
  // unrelated re-renders (e.g. typing the point size). Without this the chart
  // received fresh arrays every render and re-ran setOption — causing the
  // "chart keeps refreshing" flicker and wasted work on large data clouds.
  const rawX = useMemo(() => rawData.map((d) => d.x), [rawData]);
  const rawY = useMemo(() => rawData.map((d) => d.y), [rawData]);
  const chartX = useMemo(() => chartData.map((d) => d.x), [chartData]);
  const chartY = useMemo(() => chartData.map((d) => d.y), [chartData]);

  // Regression overlays (predicted curve + tolerance bounds) derived once per
  // chartData change rather than on every render.
  const { predicted, upper, lower } = useMemo(() => {
    const hasRegressionValues = chartData.some(
      (d) => d.regressionValue !== undefined && d.regressionValue !== null && !isNaN(d.regressionValue)
    );
    const hasUpperBounds = chartData.some(
      (d) => d.upperBound !== undefined && !isNaN(d.upperBound)
    );
    const hasLowerBounds = chartData.some(
      (d) => d.lowerBound !== undefined && !isNaN(d.lowerBound)
    );

    return {
      predicted: hasRegressionValues
        ? chartData.map((d) =>
            d.regressionValue !== undefined && d.regressionValue !== null && !isNaN(d.regressionValue)
              ? (d.regressionValue as number)
              : d.y
          )
        : undefined,
      upper: hasUpperBounds
        ? chartData.map((d) =>
            d.upperBound !== undefined && !isNaN(d.upperBound) ? d.upperBound : d.y
          )
        : undefined,
      lower: hasLowerBounds
        ? chartData.map((d) =>
            d.lowerBound !== undefined && !isNaN(d.lowerBound) ? d.lowerBound : d.y
          )
        : undefined,
    };
  }, [chartData]);

  // Show the count of points actually rendered: processed points once a
  // regression has run, otherwise the raw cloud being displayed.
  const displayedPointCount = chartData.length > 0 ? chartData.length : rawData.length;

  // Guard against an empty / non-numeric point-size field → NaN symbolSize.
  const resolvedPointSize = Math.min(Math.max(parseInt(pointSize, 10) || 6, 1), 40);

  return (
    <div 
      className="mt-4 sm:mt-6 bg-card rounded-lg shadow-sm p-3 sm:p-4 border border-border overflow-hidden"
      role="region"
      aria-label={t('chart.title')}
    >
      {/* Header with data points count and controls */}
      <div className="mb-3 sm:mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
        <div className="text-xs sm:text-sm text-muted-foreground">
          <span className="font-medium">{t('dataPoints.count')}:</span>{' '}
          {displayedPointCount}
        </div>
        <div className="flex items-center gap-2 sm:space-x-4">
          <div className="w-16 sm:w-24">
            <InputField
              label={t('chart.pointSize') + ':'}
              value={pointSize}
              onChange={onPointSizeChange}
              aria-label={t('chart.pointSize')}
            />
          </div>
        </div>
      </div>

      {/* Mobile warning - only visible on small screens */}
      <div className="block sm:hidden bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
        <div className="text-amber-600 mb-2">
          <svg
            className="w-8 h-8 mx-auto"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h3 id="data-cloud-regression-results-heading" className="text-sm font-semibold text-amber-800 mb-1">
          {t('mobileWarning.title')}
        </h3>
        <p className="text-xs text-amber-700">
          {t('mobileWarning.message')}
        </p>
      </div>

      {/* Chart rendering - hidden on mobile, visible on sm+ screens */}
      <div className="hidden sm:block">
        {rawData.length > 0 && chartData.length === 0 ? (
          // Raw data but no processed chart data yet
          <div className="relative w-full h-[570px] overflow-hidden">
            <EChartsRegressionChart
              x={rawX}
              y={rawY}
              height={500}
              pointSize={resolvedPointSize}
              xAxisLabel={xAxisLabel}
              yAxisLabel={yAxisLabel}
              yAxisFormatter={germanNumberFormatter}
            />
          </div>
        ) : chartData.length > 0 ? (
          // Processed chart data with regression
          <div className="relative w-full h-[570px] overflow-hidden">
            {isLoading && (
              <div 
                className="absolute inset-0 flex items-center justify-center bg-card/70 z-10"
                role="status"
                aria-label={t('chart.generating')}
              >
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
                  <p className="mt-2 text-sm text-muted-foreground">{t('chart.generating')}</p>
                </div>
              </div>
            )}
            <EChartsRegressionChart
              x={chartX}
              y={chartY}
              predicted={predicted}
              upper={upper}
              lower={lower}
              height={500}
              pointSize={resolvedPointSize}
              xAxisLabel={xAxisLabel}
              yAxisLabel={yAxisLabel}
              yAxisFormatter={germanNumberFormatter}
            />
          </div>
        ) : (
          // No data available
          <div className="h-[500px] w-full flex items-center justify-center text-muted-foreground text-sm">
            {t('common.noDataAvailable', { ns: 'common' })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RegressionResultsSection;
